/**
 * components/object/StructureInspector.jsx — Iter 47.1
 *
 * Tabbed structure inspector matching the directive's exact spec:
 *   TAB 1 — Overview  (engineer data + counters + status + add-note + issues)
 *   TAB 2 — Photos    (Iter 35 PhotoTimeline reused — same code path as pins)
 *   TAB 3 — Timeline  (unified ObjectTimeline)
 *
 * Mounts inside the existing InspectorBridge drawer so the slide-in,
 * Esc-dismiss, and GPS distance plumbing all stay intact (Iter 31 / 34).
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  X, AlertTriangle, Layers3, ListChecks, Camera, Activity, Eye, Plus,
  Loader2, FileText, GitBranch,
} from "lucide-react";
import {
  LAYER_PALETTE, CONFLICT_PALETTE, LAYER_LABEL, STRUCTURE_LABEL,
  INSTALL_LABEL,
} from "@/lib/operationalPalette";
import RevisionImpactPanel from "@/components/viewer/RevisionImpactPanel";
import PhotoTimeline from "@/components/viewer/PhotoTimeline";
import ObjectTimeline from "@/components/object/ObjectTimeline";
import StationChip from "@/components/object/StationChip";
import {
  ObjectNoteAdder, ObjectIssuesPanel, ObjectStatusPicker,
} from "@/components/object/ObjectQuickActions";
import ObjectRelations from "@/components/object/ObjectRelations";
import ObjectHealthPanel from "@/components/object/ObjectHealthPanel";
import ObjectDocuments from "@/components/object/ObjectDocuments";
import ObjectRevisions from "@/components/object/ObjectRevisions";
import ObjectAssignmentPicker from "@/components/object/ObjectAssignmentPicker";
import { api } from "@/lib/api";
import { fetchSummary, OBJECT_STATUS_LABEL } from "@/lib/objectIntel";
import { formatRelative } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { inspectorTabsForRole } from "@/lib/permissions";

const ALL_TABS = [
  { id: "overview", label: "Overview", icon: ListChecks },
  { id: "photos", label: "Photos", icon: Camera },
  { id: "documents", label: "Docs", icon: FileText },
  { id: "revisions", label: "Revisions", icon: GitBranch },
  { id: "timeline", label: "Timeline", icon: Activity },
];

export default function StructureInspector({
  structure, onClose, review, myAck, onAckResolved,
  distanceFromMe, sheetId, gpsContext,
}) {
  const { user } = useAuth();
  // Iter 63 P0-4 — role-aware tabs.
  const tabs = useMemo(() => {
    const allowed = new Set(inspectorTabsForRole(user));
    return ALL_TABS.filter((t) => allowed.has(t.id));
  }, [user]);
  const allowedIds = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs]);
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loadingSummary, setLoadingSummary] = useState(true);
  // Iter 67 Phase 3 — Inspector minimize pill. Declared up here so the
  // hook always runs in the same order regardless of whether the
  // `structure` prop is set yet (Rules of Hooks).
  const [minimized, setMinimized] = useState(false);

  // Keep tab valid as roles / tabs change.
  useEffect(() => {
    if (!allowedIds.has(tab)) setTab(tabs[0]?.id || "overview");
  }, [allowedIds, tab, tabs]);

  const reloadSummary = useCallback(async () => {
    if (!structure?.id) return;
    setLoadingSummary(true);
    try {
      setSummary(await fetchSummary("structure", structure.id));
    } catch (e) {
      setSummary({ error: e?.message || "failed" });
    } finally {
      setLoadingSummary(false);
    }
  }, [structure?.id]);

  useEffect(() => { reloadSummary(); }, [reloadSummary, refreshKey]);

  // Iter 53.1 — switch tab on `forgedops:inspector-tab` events so that
  // jumping from a health reason / Command-K document hit lands on the
  // correct tab inside the structure inspector.
  useEffect(() => {
    if (!structure?.id) return;
    const onSwitch = (e) => {
      const t = e.detail || {};
      if (t.object_type === "structure" && t.object_id === structure.id && t.tab) {
        if (allowedIds.has(t.tab)) setTab(t.tab);
      }
    };
    window.addEventListener("forgedops:inspector-tab", onSwitch);
    return () => window.removeEventListener("forgedops:inspector-tab", onSwitch);
  }, [structure?.id, allowedIds]);

  if (!structure) return null;
  const layerPal = LAYER_PALETTE[structure.layer_type] || {};
  const conflict = CONFLICT_PALETTE[structure.conflict_state] || CONFLICT_PALETTE.normal;
  const hasConflict = structure.conflict_state !== "normal";

  const bump = () => setRefreshKey((k) => k + 1);

  // Iter 67 Phase 3 — Inspector minimize pill. The right-side
  // inspector consumes ~40% of an iPad portrait canvas; minimizing
  // collapses it to a 44 × 44 px floating tab the user can re-expand
  // with one tap. State preserved (tab + scroll).
  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        data-testid="structure-inspector-restore"
        className="absolute top-3 right-3 z-30 inline-flex items-center gap-2 min-h-[44px] px-3 rounded-full bg-[var(--fop-primary-soft)] border border-[var(--fop-primary)] text-[var(--fop-primary)] font-mono text-[10px] uppercase tracking-wider shadow-lg hover:bg-[var(--fop-cta-bg)] hover:text-white transition-colors"
        title="Expand inspector"
      >
        <Eye size={12} /> {structure.structure_label}
      </button>
    );
  }

  return (
    <div className="flex flex-col h-full" data-testid="structure-inspector"
          data-structure-id={structure.id}>
      {/* Identity */}
      <div className="px-4 py-3 border-b border-[var(--fop-border)] flex items-start gap-3">
        <span
          className="inline-block w-3 h-3 rounded-sm border mt-1"
          style={{ background: layerPal.swatch, borderColor: layerPal.swatch }}
          aria-hidden="true"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)]">
            <Layers3 size={9} />
            <span>{LAYER_LABEL[structure.layer_type] || structure.layer_type}</span>
            <span className="text-[var(--fop-text-3)]/40">·</span>
            <span>{STRUCTURE_LABEL[structure.structure_type] || structure.structure_type}</span>
          </div>
          <div className="font-mono text-sm font-bold text-[var(--fop-text)] mt-0.5"
                data-testid="inspector-structure-label">
            {structure.structure_label}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] h-9 w-9 flex items-center justify-center rounded-md hover:bg-[var(--fop-surface-2)]"
            aria-label="Minimize inspector"
            data-testid="structure-inspector-minimize"
            title="Minimize"
          >
            {/* Down-arrow icon → minimize to pill */}
            <span className="text-[14px] leading-none">⌄</span>
          </button>
          <button onClick={onClose}
                   className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)] h-9 w-9 flex items-center justify-center rounded-md hover:bg-[var(--fop-surface-2)] -mr-1"
                   aria-label="Close inspector" data-testid="inspector-close">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Counter strip — matches directive success criteria */}
      <SummaryStrip summary={summary} loading={loadingSummary}
                     distanceFromMe={distanceFromMe} structure={structure}
                     sheetId={sheetId} />

      {/* Conflict callout */}
      {hasConflict && (
        <div className="px-4 py-2 border-b border-[var(--fop-border)] flex items-start gap-2 text-[11px]"
              style={{ background: conflict.ring ? `${conflict.ring.replace("0.95", "0.08").replace("0.90", "0.08")}` : "transparent" }}
              data-testid="inspector-conflict-callout">
          <AlertTriangle size={12} style={{ color: conflict.ring }} className="mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-wider" style={{ color: conflict.ring }}>
              {conflict.label}
            </div>
            {structure.notes && (
              <div className="text-[var(--fop-text-2)] mt-0.5 leading-snug">
                {structure.notes}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs — role-aware (Iter 63 P0-4) */}
      <div className="flex border-b border-[var(--fop-border)] px-2 shrink-0"
            data-testid="structure-tabs">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`structure-tab-${t.id}`}
              className={`px-3 py-2.5 text-xs font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                active
                  ? "text-[var(--fop-primary)] border-[var(--fop-primary)]"
                  : "text-[var(--fop-text-3)] border-transparent hover:text-[var(--fop-text)]"
              }`}
            >
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto">
        {tab === "overview" && (
          <OverviewTab
            structure={structure} sheetId={sheetId} onChanged={bump}
            summary={summary} review={review} myAck={myAck}
            onAckResolved={onAckResolved} refreshKey={refreshKey}
          />
        )}
        {tab === "photos" && (
          <div className="p-4" data-testid="structure-tab-photos-body">
            <PhotoTimeline
              originType="structure"
              originId={structure.id}
              originLabel={structure.structure_label}
              sheetId={sheetId}
              normalizedX={structure.normalized_x}
              normalizedY={structure.normalized_y}
              gpsLat={gpsContext?.lat}
              gpsLon={gpsContext?.lon}
              gpsAccuracy={gpsContext?.accuracy}
            />
          </div>
        )}
        {tab === "documents" && (
          <div className="p-4" data-testid="structure-tab-documents-body">
            <ObjectDocuments objectType="structure" objectId={structure.id}
                              onChanged={bump} />
          </div>
        )}
        {tab === "revisions" && (
          <div className="p-4" data-testid="structure-tab-revisions-body">
            <ObjectRevisions objectType="structure" objectId={structure.id}
                              refreshKey={refreshKey} />
          </div>
        )}
        {tab === "timeline" && (
          <div className="p-4" data-testid="structure-tab-timeline-body">
            <ObjectTimeline objectType="structure" objectId={structure.id}
                             refreshKey={refreshKey} />
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryStrip({ summary, loading, distanceFromMe, structure, sheetId }) {
  const ft = (typeof distanceFromMe === "function"
              && structure?.normalized_x != null
              && structure?.normalized_y != null)
    ? distanceFromMe(structure.normalized_x, structure.normalized_y)
    : null;
  const distance = (ft != null && Number.isFinite(ft))
    ? (ft < 10 ? `${ft.toFixed(1)} ft`
       : ft < 1000 ? `${Math.round(ft)} ft`
       : `${(ft / 5280).toFixed(2)} mi`)
    : null;
  return (
    <div className="px-4 py-2 border-b border-[var(--fop-border)] flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)]"
          data-testid="structure-summary-strip">
      {loading && <span><Loader2 size={10} className="inline animate-spin mr-1" />…</span>}
      {!loading && summary && !summary.error && (
        <>
          <span data-testid="counter-status">
            Status:{" "}
            <b className="text-[var(--fop-primary)] font-semibold">
              {summary.status ? (OBJECT_STATUS_LABEL[summary.status] || summary.status) : "—"}
            </b>
          </span>
          <span data-testid="counter-photos">
            Photos:{" "}
            <b className="text-[var(--fop-text)] font-semibold">{summary.photos}</b>
          </span>
          <span data-testid="counter-notes">
            Notes:{" "}
            <b className="text-[var(--fop-text)] font-semibold">{summary.notes}</b>
          </span>
          <span data-testid="counter-issues">
            Issues:{" "}
            <b className="text-[var(--fop-text)] font-semibold">{summary.issues_open}</b>
            {summary.issues_total > summary.issues_open && (
              <span className="opacity-60"> / {summary.issues_total}</span>
            )}
          </span>
          {summary.last_activity_at && (
            <span data-testid="counter-last-activity">
              Last: <b className="text-[var(--fop-text-2)] font-semibold">{formatRelative(summary.last_activity_at)}</b>
            </span>
          )}
        </>
      )}
      {distance && (
        <span className="ml-auto" data-testid="inspector-distance-from-me"
               data-distance-ft={Math.round(ft)}>
          From you: <b className="text-[var(--fop-primary)] font-semibold">→ {distance}</b>
        </span>
      )}
      {sheetId && structure?.normalized_x != null && structure?.normalized_y != null && (
        <span className="w-full mt-0.5 -mb-0.5">
          <StationChip
            sheetId={sheetId}
            nx={structure.normalized_x}
            ny={structure.normalized_y}
            compact
            testId="structure-station-chip"
          />
        </span>
      )}
    </div>
  );
}

function OverviewTab({ structure, sheetId, onChanged, summary, review, myAck, onAckResolved, refreshKey }) {
  return (
    <div className="p-4 space-y-4" data-testid="structure-tab-overview-body">
      {/* Iter 53.1 — Object Health Panel mounted as the FIRST Overview
          card. Tapping a health reason switches the inspector to the
          source tab (Documents / Timeline / etc) via the
          `forgedops:inspector-tab` event the parent listens for. */}
      <ObjectHealthPanel objectType="structure" objectId={structure.id}
                          refreshKey={refreshKey} />

      {/* Engineering fields — same as legacy */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-[11px]"
            data-testid="inspector-fields">
        <Field label="Rim Elev" value={fmtElev(structure.rim_elevation)} mono />
        <Field label="Install" value={INSTALL_LABEL[structure.install_status] || structure.install_status} />
        {structure.invert_in != null && <Field label="Inv In" value={fmtElev(structure.invert_in)} mono />}
        {structure.invert_out != null && <Field label="Inv Out" value={fmtElev(structure.invert_out)} mono />}
        {structure.pipe_size && <Field label="Pipe Size" value={structure.pipe_size} mono />}
        {structure.slope != null && <Field label="Slope" value={`${structure.slope.toFixed(2)}%`} mono />}
        {structure.material && (
          <div className="col-span-2">
            <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-0.5">Material</div>
            <div className="text-[var(--fop-text)]">{structure.material}</div>
          </div>
        )}
      </div>

      {structure.revision_state && (
        <div data-testid="inspector-revision-state">
          <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-0.5">Revision</div>
          <div className="text-[11px] text-[var(--fop-text-2)] leading-snug">{structure.revision_state}</div>
        </div>
      )}

      {review && (
        <RevisionImpactPanel review={review} structure={structure}
                              myAck={myAck} onResolved={onAckResolved} />
      )}

      <ObjectStatusPicker objectType="structure" objectId={structure.id}
                           onChanged={onChanged} />
      <ObjectAssignmentPicker objectType="structure" objectId={structure.id}
                                onChanged={onChanged} />

      <div className="fop-surface p-3 rounded-md">
        <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] mb-2 flex items-center gap-1.5">
          <Activity size={12} /> Add Operational Note
        </div>
        <ObjectNoteAdder objectType="structure" objectId={structure.id}
                          onAdded={onChanged} />
      </div>

      <ObjectIssuesPanel objectType="structure" objectId={structure.id}
                          onChanged={onChanged} />

      <ObjectRelations objectType="structure" objectId={structure.id}
                        refreshKey={onChanged ? undefined : 0} />

      <CreateUtilityWatchAction
        structure={structure} sheetId={sheetId} onCreated={onChanged}
      />
    </div>
  );
}

function CreateUtilityWatchAction({ structure, sheetId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    const t = title.trim() || `Watch — ${structure.structure_label}`;
    setBusy(true);
    setErr(null);
    try {
      await api.post("/utility_watches", {
        sheet_id: sheetId,
        title: t,
        source_object_type: "structure",
        source_object_id: structure.id,
        layer_type: structure.layer_type,
        normalized_x: structure.normalized_x,
        normalized_y: structure.normalized_y,
      });
      setOpen(false);
      setTitle("");
      onCreated && onCreated();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div data-testid="create-utility-watch">
        <button
          className="btn btn-ghost text-xs w-full justify-center"
          onClick={() => setOpen(true)}
          data-testid="open-create-watch"
        >
          <Eye size={12} /> Create Utility Watch
        </button>
      </div>
    );
  }

  return (
    <div className="fop-surface p-3 rounded-md space-y-2"
          data-testid="utility-watch-form">
      <div className="text-xs uppercase tracking-wider font-mono text-[var(--fop-text-3)] flex items-center gap-1.5">
        <Eye size={12} /> Create Utility Watch
      </div>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={`Watch — ${structure.structure_label}`}
        maxLength={200}
        className="w-full text-sm"
        autoFocus
        data-testid="utility-watch-title"
      />
      <div className="flex gap-2">
        <button
          className="btn btn-primary text-xs"
          onClick={submit}
          disabled={busy}
          data-testid="utility-watch-submit"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Create
        </button>
        <button
          className="btn btn-ghost text-xs"
          onClick={() => { setOpen(false); setTitle(""); }}
          data-testid="utility-watch-cancel"
        >
          Cancel
        </button>
      </div>
      {err && <div className="text-[11px] text-[var(--fop-danger)]">{err}</div>}
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-0.5">
        {label}
      </div>
      <div className={`text-[var(--fop-text)] ${mono ? "font-mono" : ""}`}>
        {value ?? <em className="text-[var(--fop-text-3)] not-italic font-mono text-[10px]">—</em>}
      </div>
    </div>
  );
}

function fmtElev(v) {
  if (v == null || !Number.isFinite(v)) return null;
  return `${v.toFixed(2)} ft`;
}
